/**
 * 匯入硬體序號 (/roster/import, boards/Import.dc.html) — admin+. Four steps:
 *   選擇檔案 → 對應欄位 → 比對預覽 → 完成.
 * The file is parsed in the browser (CSV / .xlsx, ≤ 5,000 rows) and never uploaded as a file;
 * the normalized ImportRow[] goes to POST /whitelist/import/preview, which classifies AND stages it
 * server-side (preview_id, ≤200 rows per shard, kept ≤24 h). Writing is segmented: /commit opens the
 * batch and writes shard 0, then /continue writes one shard per call until `done` (第 x / y 段).
 * A failed shard leaves no trace and the batch waits at it, so 「繼續匯入」 just continues the same
 * batch. Already-revoked machines stay revoked unless ticked (restore_sns). The idempotency key is
 * minted once per file so a retried commit cannot open a second batch.
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { ToneTag } from '@/components/RosterBits';
import { errMsg } from '@/components/Dialogs';
import { api, isApiError } from '@/lib/api';
import { runImport, skipBreakdown, writingLabel, type ImportProgress } from '@/lib/importRun';
import { buildCsv, downloadCsv } from '@/lib/csv';
import {
  IMPORT_FIELDS,
  MAX_IMPORT_ROWS,
  buildImportRows,
  columnLetter,
  defaultBatchName,
  detectLayout,
  sampleColumn,
  type BuiltRows,
  type ColumnMapping,
  type ImportField,
  type SheetLayout,
} from '@/lib/importMap';
import { readSheetFile } from '@/lib/sheet';
import { IMPORT_FIELD_LABEL, PREVIEW_LABEL, PREVIEW_ORDER, PREVIEW_TONE, describeChanges, importReasonLabel } from '@/lib/labels';
import type { ImportPreview, ImportShardResult, PreviewCategory, PreviewRow } from '@/types';

type Step = 'upload' | 'map' | 'preview' | 'done';
const STEPS: { id: Step; label: string }[] = [
  { id: 'upload', label: '選擇檔案' },
  { id: 'map', label: '對應欄位' },
  { id: 'preview', label: '比對預覽' },
  { id: 'done', label: '完成' },
];

const ACCEPT = '.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const PREVIEW_PAGE = 200;

const newKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface Loaded {
  fileName: string;
  sheetName?: string;
  rows: string[][];
  layout: SheetLayout;
}

export function RosterImport() {
  const [step, setStep] = useState<Step>('upload');
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [built, setBuilt] = useState<BuiltRows | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [restore, setRestore] = useState<Set<string>>(new Set());
  const [batchName, setBatchName] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState<ImportProgress | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [stalled, setStalled] = useState(false); // a shard failed; 繼續匯入 resumes the batch
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const idemKey = useRef(newKey());

  const reset = () => {
    setStep('upload'); setLoaded(null); setMapping(null); setBuilt(null); setPreview(null);
    setRestore(new Set()); setBatchName(''); setConfirmText(''); setResult(null); setErr(null);
    setProgress(null); setStalled(false);
    idemKey.current = newKey();
  };

  const onFile = async (file: File) => {
    setErr(null); setBusy(true);
    try {
      if (!/\.(csv|xlsx)$/i.test(file.name)) throw new Error('只接受 .csv 或 .xlsx 檔案');
      const read = await readSheetFile(file);
      const layout = detectLayout(read.rows);
      const dataRows = read.rows.slice(layout.dataStart).filter((r) => r.some((c) => c.trim() !== '')).length;
      if (dataRows === 0) throw new Error('檔案裡沒有資料列');
      if (dataRows > MAX_IMPORT_ROWS) throw new Error(`檔案有 ${dataRows} 列,超過單次上限 ${MAX_IMPORT_ROWS.toLocaleString()} 列;請分批匯入`);
      setLoaded({ fileName: file.name, sheetName: read.sheetName, rows: read.rows, layout });
      setMapping(layout.mapping);
      setBatchName(defaultBatchName(file.name));
      idemKey.current = newKey();
      setStep('map');
    } catch (e) {
      setErr(`讀不到這個檔案:${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    if (!loaded || !mapping) return;
    if (mapping.sn === null) { setErr('請指定「硬體序號」對應的欄'); return; }
    const b = buildImportRows(loaded.rows, mapping, loaded.layout.dataStart);
    if (b.rows.length === 0) { setErr('對應後沒有任何資料列'); return; }
    if (b.rows.length > MAX_IMPORT_ROWS) { setErr(`超過單次上限 ${MAX_IMPORT_ROWS.toLocaleString()} 列`); return; }
    setBusy(true); setErr(null);
    try {
      const p = await api.importPreview(b.rows);
      setBuilt(b); setPreview(p); setRestore(new Set()); setConfirmText('');
      setProgress(null); setStalled(false);
      idemKey.current = newKey(); // a new preview is a new batch
      setStep('preview');
    } catch (e) {
      setErr(`比對失敗:${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const writeCount = preview ? (preview.summary.add ?? 0) + (preview.summary.update ?? 0) + restore.size : 0;

  // Runs the batch to the end from `first` (the commit, or a continue when resuming).
  const drive = async (first: () => Promise<ImportShardResult>) => {
    setBusy(true); setErr(null); setStalled(false);
    try {
      const out = await runImport(first, (id) => api.importContinue(id), setProgress, progress?.skipped ?? []);
      setResult(out);
      setStep('done');
    } catch (e) {
      if (isApiError(e) && (e.code === 'preview_not_found' || e.code === 'import_expired')) {
        setErr('這份比對結果已過期(保留 24 小時),請重新比對再寫入。已寫入的列會保留。');
        setPreview(null); setProgress(null);
        setStep('map');
      } else {
        setErr(`寫入中斷:${errMsg(e)}。已寫入的段落會保留,按「繼續匯入」從中斷處接著寫。`);
        setStalled(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const commit = () => {
    if (!preview || !loaded) return;
    void drive(() =>
      api.importCommit({
        preview_id: preview.preview_id,
        batch_name: batchName.trim(),
        file_name: loaded.fileName,
        restore_sns: Array.from(restore),
        idempotency_key: idemKey.current,
      }),
    );
  };

  // Resume: continue the known batch; if the commit itself never answered, replaying it with the
  // same idempotency key returns (or finishes) that same batch.
  const resume = () => {
    const batchId = progress?.last.batch_id;
    if (batchId) void drive(() => api.importContinue(batchId));
    else commit();
  };

  const idx = STEPS.findIndex((s) => s.id === step);

  return (
    <div className="v2-page">
      <div className="v2-head">
        <div className="titles">
          <Link to="/roster" className="v2-back">← 硬體序號名單</Link>
          <h1 className="v2-title">匯入硬體序號</h1>
        </div>
      </div>

      <ol className="steps" aria-label="匯入步驟">
        {STEPS.map((s, i) => (
          <li key={s.id} className={i === idx ? 'on' : i < idx ? 'done' : ''} aria-current={i === idx ? 'step' : undefined}>
            <span className="dot">{i + 1}</span>{s.label}
          </li>
        ))}
      </ol>

      {err && (
        <div className="notice-warn row" role="alert" style={{ gap: 'var(--space-3)', alignItems: 'center' }}>
          <span className="grow">{err}</span>
          {stalled && step === 'preview' && (
            <button type="button" className="btn btn-primary" onClick={resume} disabled={busy}>繼續匯入</button>
          )}
        </div>
      )}

      {step === 'upload' && <UploadStep busy={busy} onFile={(f) => void onFile(f)} />}

      {step === 'map' && loaded && mapping && (
        <MapStep
          loaded={loaded}
          mapping={mapping}
          onMapping={setMapping}
          busy={busy}
          onBack={reset}
          onNext={() => void runPreview()}
        />
      )}

      {step === 'preview' && preview && built && (
        <PreviewStep
          preview={preview}
          lines={built.lines}
          restore={restore}
          onRestore={setRestore}
          batchName={batchName}
          onBatchName={setBatchName}
          confirmText={confirmText}
          onConfirmText={setConfirmText}
          writeCount={writeCount}
          busy={busy}
          progress={progress}
          locked={busy || stalled || progress !== null}
          onBack={() => { setStep('map'); setErr(null); }}
          onCommit={commit}
        />
      )}

      {step === 'done' && result && (
        <DoneStep result={result} batchName={batchName.trim()} lines={built?.lines ?? []} onAgain={reset} />
      )}
    </div>
  );
}

function UploadStep({ busy, onFile }: { busy: boolean; onFile: (f: File) => void }) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  return (
    <section className="panel" style={{ alignItems: 'center', padding: 'var(--space-10) var(--space-5)' }}>
      <div
        className={'dropzone' + (over ? ' over' : '')}
        role="button"
        tabIndex={0}
        aria-label="選擇或拖放 CSV / Excel 檔案"
        onClick={() => input.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) onFile(f);
        }}
      >
        <Icon name="upload" size={28} />
        <span className="big">{busy ? '讀取中…' : '把 CSV 或 Excel(.xlsx)拖到這裡'}</span>
        <span>檔案只在瀏覽器裡讀取,不會上傳原檔 · 最多 5,000 列</span>
        <span className="btn btn-outline" style={{ marginTop: 'var(--space-2)' }}>選擇檔案</span>
      </div>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onFile(f);
        }}
      />
    </section>
  );
}

function MapStep({
  loaded, mapping, onMapping, busy, onBack, onNext,
}: {
  loaded: Loaded; mapping: ColumnMapping; onMapping: (m: ColumnMapping) => void;
  busy: boolean; onBack: () => void; onNext: () => void;
}) {
  const { layout, rows } = loaded;
  const dataRows = rows.slice(layout.dataStart).filter((r) => r.some((c) => c.trim() !== '')).length;
  const auto = layout.headerRow !== null ? '已自動對應欄位,可修改' : '沒有標題列,第一欄視為硬體序號,可修改';
  const setField = (f: ImportField, v: string) => {
    const col = v === '' ? null : Number(v);
    const next: ColumnMapping = { ...mapping, [f]: col };
    // One column feeds one field: taking a column away from another field clears that field.
    if (col !== null) for (const other of IMPORT_FIELDS) if (other !== f && next[other] === col) next[other] = null;
    onMapping(next);
  };
  const label = (f: ImportField) => (f === 'sn' ? '硬體序號(必填)' : IMPORT_FIELD_LABEL[f]);
  return (
    <section className="panel flush">
      <div className="panel-head">
        <span className="muted-note">
          {loaded.fileName}
          {loaded.sheetName ? ` · 工作表「${loaded.sheetName}」` : ''} · {dataRows} 列 · {auto}
        </span>
      </div>
      <div style={{ padding: '0 var(--space-5) var(--space-4)' }}>
        <div className="map-grid">
          <span className="hd">名單欄位</span>
          <span className="hd">檔案中的欄</span>
          <span className="hd">前三列</span>
          {IMPORT_FIELDS.map((f) => (
            <MapRow key={f} label={label(f)} value={mapping[f]} headers={layout.headers} onChange={(v) => setField(f, v)} sample={sampleColumn(rows, mapping[f], layout.dataStart)} required={f === 'sn'} />
          ))}
        </div>
      </div>
      <div className="wizard-foot">
        <button type="button" className="btn btn-outline" onClick={onBack} disabled={busy}>上一步</button>
        <span className="grow" />
        <button type="button" className="btn btn-primary" onClick={onNext} disabled={busy || mapping.sn === null}>
          {busy ? '比對中…' : '和現有名單比對'}
        </button>
      </div>
    </section>
  );
}

function MapRow({
  label, value, headers, onChange, sample, required,
}: {
  label: string; value: number | null; headers: string[]; onChange: (v: string) => void; sample: string[]; required: boolean;
}) {
  return (
    <>
      <span>{label}</span>
      <select className="v2-select" aria-label={`${label} 對應的欄`} value={value === null ? '' : String(value)} onChange={(e) => onChange(e.target.value)}>
        {!required && <option value="">(不匯入)</option>}
        {required && value === null && <option value="">請選擇</option>}
        {headers.map((h, i) => (
          <option key={i} value={i}>{`${columnLetter(i)} · ${h}`}</option>
        ))}
      </select>
      <span className="sample" title={sample.join(', ')}>{sample.join(', ')}</span>
    </>
  );
}

function rowNote(r: PreviewRow): string {
  switch (r.category) {
    case 'add': return '新機器';
    case 'update': return describeChanges(r.changes) || '欄位有變動';
    case 'same': return '欄位都相同';
    case 'revoked': return '已由後台撤銷';
    case 'invalid': return importReasonLabel(r.error) || '格式錯誤';
    case 'duplicate': return r.error && r.error !== 'duplicate' ? importReasonLabel(r.error) : '與前面的列重複';
    default: return '';
  }
}

function rowAction(r: PreviewRow): string {
  switch (r.category) {
    case 'add': return '加入名單(來源:批次匯入)';
    case 'update': return r.origin === 'serial' ? '只更新欄位,來源維持序號兌換' : '只更新擁有人 / 團隊等欄位';
    case 'same': case 'invalid': case 'duplicate': return '略過';
    default: return '';
  }
}

function PreviewStep({
  preview, lines, restore, onRestore, batchName, onBatchName, confirmText, onConfirmText, writeCount, busy, progress, locked, onBack, onCommit,
}: {
  preview: ImportPreview; lines: number[]; restore: Set<string>; onRestore: (s: Set<string>) => void;
  batchName: string; onBatchName: (v: string) => void; confirmText: string; onConfirmText: (v: string) => void;
  writeCount: number; busy: boolean; progress: ImportProgress | null;
  /** Once writing started the batch is fixed: no edits, no second commit (resume instead). */
  locked: boolean; onBack: () => void; onCommit: () => void;
}) {
  const [filter, setFilter] = useState<PreviewCategory | null>(null);
  const [shown, setShown] = useState(PREVIEW_PAGE);
  const lineOf = (i: number) => lines[i] ?? i + 1;
  const rows = useMemo(() => (filter ? preview.rows.filter((r) => r.category === filter) : preview.rows), [preview.rows, filter]);
  const nameOk = batchName.trim().length >= 1 && batchName.trim().length <= 80;
  const confirmed = confirmText.trim() === String(writeCount);
  const toggle = (sn: string) => {
    const n = new Set(restore);
    if (n.has(sn)) n.delete(sn);
    else n.add(sn);
    onRestore(n);
  };
  return (
    <section className="panel flush">
      <div className="panel-head" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
        {PREVIEW_ORDER.map((c) => (
          <button
            key={c}
            type="button"
            className={`chip chip-${PREVIEW_TONE[c]}` + (filter === c ? ' ring' : '')}
            aria-pressed={filter === c}
            onClick={() => { setFilter(filter === c ? null : c); setShown(PREVIEW_PAGE); }}
          >
            {c === 'revoked' ? '已撤銷(預設不復原)' : PREVIEW_LABEL[c]} {preview.summary[c] ?? 0}
          </button>
        ))}
      </div>
      <div className="tscroll" style={{ maxHeight: 460 }}>
        <table className="v2-table">
          <thead>
            <tr><th style={{ width: 64 }}>列</th><th>硬體序號</th><th>比對結果</th><th>說明</th><th>處理方式</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr className="empty-row"><td colSpan={5}>沒有這一類的列</td></tr>
            ) : (
              rows.slice(0, shown).map((r) => (
                <tr key={`${r.index}-${r.sn}`}>
                  <td className="muted num">{lineOf(r.index)}</td>
                  <td className="mono">{r.sn || '—'}</td>
                  <td><ToneTag tone={PREVIEW_TONE[r.category]}>{PREVIEW_LABEL[r.category]}</ToneTag></td>
                  <td className="ellipsis" title={rowNote(r)}>{rowNote(r)}</td>
                  <td>
                    {r.category === 'revoked' ? (
                      <label className="opt-row">
                        <input type="checkbox" checked={restore.has(r.sn)} disabled={locked} onChange={() => toggle(r.sn)} />
                        復原這台(預設不復原)
                      </label>
                    ) : (
                      <span className="muted">{rowAction(r)}</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {rows.length > shown && (
          <div style={{ padding: 'var(--space-3) var(--space-4)' }}>
            <button type="button" className="link" onClick={() => setShown((n) => n + PREVIEW_PAGE)}>
              再顯示 {Math.min(PREVIEW_PAGE, rows.length - shown)} 列(共 {rows.length} 列)
            </button>
          </div>
        )}
      </div>
      {progress && (
        <div className="stack" style={{ gap: 'var(--space-2)', padding: '12px var(--space-5) 0' }} role="status" aria-live="polite">
          <span className="muted-note">
            {writingLabel(progress.last, busy)} · 新增 {progress.last.added} · 更新 {progress.last.updated}
            {progress.last.restored ? ` · 復原 ${progress.last.restored}` : ''} · 略過 {progress.last.skipped}
          </span>
          <div
            className="bar-track"
            role="progressbar"
            aria-label="寫入進度"
            aria-valuemin={0}
            aria-valuemax={progress.last.shard_count}
            aria-valuenow={progress.last.shard + 1}
          >
            <div className="bar-fill" style={{ width: `${Math.round(((progress.last.shard + 1) / progress.last.shard_count) * 100)}%` }} />
          </div>
        </div>
      )}
      <div className="wizard-foot">
        <label className="inline-field">批次名稱
          <input className="v2-input" style={{ width: 260 }} value={batchName} maxLength={80} disabled={locked} onChange={(e) => onBatchName(e.target.value)} placeholder="ASUS 2026Q4 展場名單" />
        </label>
        <label className="inline-field">輸入要寫入的筆數以確認
          <input className="v2-input" style={{ width: 90 }} inputMode="numeric" value={confirmText} disabled={locked} onChange={(e) => onConfirmText(e.target.value)} />
        </label>
        <span className="grow" />
        <button type="button" className="btn btn-outline" onClick={onBack} disabled={locked}>上一步</button>
        <button type="button" className="btn btn-primary" onClick={onCommit} disabled={locked || !nameOk || !confirmed || writeCount === 0}>
          {busy ? (progress ? writingLabel(progress.last, true) : '寫入中…') : `寫入 ${writeCount} 筆`}
        </button>
      </div>
    </section>
  );
}

function DoneStep({
  result, batchName, lines, onAgain,
}: {
  result: ImportProgress; batchName: string; lines: number[]; onAgain: () => void;
}) {
  const lineOf = (i: number) => lines[i] ?? i + 1;
  const totals = result.last;
  // From the rows the Worker actually skipped (every shard's), so the buckets always sum to 略過.
  const breakdown = skipBreakdown(result.skipped);
  const downloadSkipped = () => {
    const csv = buildCsv(
      ['列', '硬體序號', '原因'],
      result.skipped.map((r) => [String(lineOf(r.index)), r.sn, importReasonLabel(r.reason)]),
    );
    downloadCsv(`略過的列-${batchName || totals.batch_id}.csv`, csv);
  };
  return (
    <section className="panel" style={{ alignItems: 'center', textAlign: 'center', padding: 'var(--space-10) var(--space-5)', gap: 'var(--space-3)' }}>
      <h2 className="panel-title" role="status">已寫入「{batchName}」</h2>
      <p className="muted-note" style={{ margin: 0, maxWidth: 720 }}>
        新增 {totals.added} 台 · 更新欄位 {totals.updated} 台
        {totals.restored ? ` · 復原 ${totals.restored} 台` : ''} · 略過 {totals.skipped} 列{breakdown}
      </p>
      <div className="row" style={{ gap: 'var(--space-3)', justifyContent: 'center' }}>
        <Link className="btn btn-primary" to={`/roster?batch=${encodeURIComponent(totals.batch_id)}`}>查看這個批次</Link>
        {result.skipped.length > 0 && (
          <button type="button" className="btn btn-outline" onClick={downloadSkipped}>
            下載略過的 {result.skipped.length} 列(CSV)
          </button>
        )}
        <button type="button" className="btn btn-outline" onClick={onAgain}>再匯入一份</button>
      </div>
    </section>
  );
}
